/**
 * Contactos de la base (`/activa/oficial/base/contactos`): buscador, filtros y la tabla paginada.
 *
 * - Todo filtro va en la URL (`?q=&etiqueta=&carga=&consentimiento=&noContactar=&sucursal=&page=`): un
 *   enlace o un F5 muestran lo mismo, y «Ver los contactos de esta carga» llega con `?carga=`.
 * - Los teléfonos se muestran enmascarados (el backend ya los manda así; `destinoParaMostrar` los vuelve a
 *   enmascarar por si alguno llegara completo). El número completo sólo se ve desde la ficha, y queda
 *   registrado.
 * - Una respuesta vieja (se tipeó rápido o se cambiaron dos filtros seguidos) no pisa a la nueva.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Search, Users } from "lucide-react";
import type { ConsentimientoContacto, OpcionesBase, PaginaContactos } from "../../types.base";
import { baseApi } from "../../baseApi";
import { destinoParaMostrar } from "../../logic/formato";
import { fecha, num } from "../../../campana-nueva/utils/format";
import { CajaError, Esqueleto, R_BASE, llamar, textoFallo } from "../../components/base/comun";
import { ChipConsentimiento } from "../../components/base/ChipConsentimiento";

const LIMITE = 50;
const FILTROS = ["q", "etiqueta", "carga", "consentimiento", "noContactar", "sucursal"] as const;
const CONSENTIMIENTOS: { valor: ConsentimientoContacto; label: string }[] = [
  { valor: "si", label: "Con consentimiento" }, { valor: "sin_dato", label: "Sin dato" }, { valor: "no", label: "Dijeron que no" },
];
const esConsentimiento = (v: string | null): v is ConsentimientoContacto => v === "si" || v === "no" || v === "sin_dato";

export default function ContactosPage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const aviso = (location.state as { aviso?: string } | null)?.aviso || "";

  const q = sp.get("q") || "";
  const page = Math.max(1, Number.parseInt(sp.get("page") || "1", 10) || 1);
  const hayFiltros = FILTROS.some((k) => !!sp.get(k));

  const [texto, setTexto] = useState(q);
  const [datos, setDatos] = useState<PaginaContactos | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [opciones, setOpciones] = useState<OpcionesBase | null>(null);
  const [reintento, setReintento] = useState(0);
  const seq = useRef(0);

  const ids = { q: useId(), etiqueta: useId(), carga: useId(), consentimiento: useId(), noContactar: useId(), sucursal: useId() };

  /** Cambia un filtro de la URL y vuelve a la primera página. */
  const setFiltro = useCallback((k: (typeof FILTROS)[number], v: string) => {
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      if (v) n.set(k, v); else n.delete(k);
      n.delete("page");
      return n;
    }, { replace: true });
  }, [setSp]);

  // El texto del buscador sigue a la URL (p. ej. «Limpiar filtros»).
  useEffect(() => { setTexto(q); }, [q]);

  // Debounce de 400 ms del buscador.
  useEffect(() => {
    if (texto.trim() === q) return;
    const t = setTimeout(() => setFiltro("q", texto.trim().slice(0, 100)), 400);
    return () => clearTimeout(t);
  }, [texto, q, setFiltro]);

  useEffect(() => {
    void (async () => {
      const l = await llamar<OpcionesBase>(() => baseApi.opciones());
      if (l.ok && l.data) setOpciones(l.data);
    })();
  }, []);

  const clave = sp.toString();
  useEffect(() => {
    const mio = ++seq.current;
    setCargando(true);
    void (async () => {
      const consentimiento = sp.get("consentimiento");
      const noContactar = sp.get("noContactar");
      const l = await llamar<PaginaContactos>(() => baseApi.contactos({
        q: q || undefined,
        etiqueta: sp.get("etiqueta") || undefined,
        carga: sp.get("carga") || undefined,
        consentimiento: esConsentimiento(consentimiento) ? consentimiento : undefined,
        noContactar: noContactar === "si" || noContactar === "no" ? noContactar : undefined,
        sucursal: sp.get("sucursal") || undefined,
        page,
        limit: LIMITE,
      }));
      if (mio !== seq.current) return;
      setCargando(false);
      if (!l.ok || !l.data) { setError(l.ok ? "El servidor no devolvió los contactos." : textoFallo(l)); return; }
      setDatos(l.data);
      setError("");
    })();
    // `clave` resume todos los parámetros de la URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, reintento]);

  // Un `?page=N` que ya no existe (se guardó cuando había más contactos y en el medio se suprimieron
  // o se borró una carga) trae `items: []` con `total > 0`: se vuelve a la última página que existe,
  // reemplazando la URL. Sólo hacia atrás: con `ultima >= page` no hay a dónde ir y no se entra en loop.
  useEffect(() => {
    if (cargando || !datos || datos.items.length || datos.total <= 0) return;
    const ultima = Math.max(1, Math.ceil(datos.total / (datos.limit || LIMITE)));
    if (ultima >= page) return;
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      if (ultima > 1) n.set("page", String(ultima)); else n.delete("page");
      return n;
    }, { replace: true });
  }, [cargando, datos, page, setSp]);

  const irAPagina = (p: number) => {
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      if (p > 1) n.set("page", String(p)); else n.delete("page");
      return n;
    });
  };
  const limpiar = () => { setTexto(""); setSp(new URLSearchParams(), { replace: true }); };

  const total = datos?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / (datos?.limit || LIMITE)));
  const selectCls = "h-10 rounded-xl border border-slate-200 px-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 w-full";

  return (
    <div className="space-y-4">
      {aviso && (
        <p className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" role="status">
          <CheckCircle2 size={15} /> {aviso}
        </p>
      )}

      <div className="space-y-3">
        <div className="relative">
          <label htmlFor={ids.q} className="sr-only">Buscar contactos</label>
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input id={ids.q} value={texto} maxLength={100} onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar por nombre, email o últimos dígitos del teléfono"
            className="w-full h-10 rounded-xl border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
        <div className="grid gap-2 grid-cols-2 md:grid-cols-5">
          <Filtro id={ids.etiqueta} label="Etiqueta">
            <select id={ids.etiqueta} value={sp.get("etiqueta") || ""} onChange={(e) => setFiltro("etiqueta", e.target.value)} className={selectCls}>
              <option value="">Todas</option>
              {(opciones?.etiquetas || []).map((o) => <option key={o.clave} value={o.clave}>{o.texto}</option>)}
            </select>
          </Filtro>
          <Filtro id={ids.carga} label="Carga">
            <select id={ids.carga} value={sp.get("carga") || ""} onChange={(e) => setFiltro("carga", e.target.value)} className={selectCls}>
              <option value="">Todas</option>
              {(opciones?.cargas || []).map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}
              {/* Una carga de la URL que no está en las opciones (todavía no cargaron) se muestra igual: si no, el select mentiría «Todas». */}
              {sp.get("carga") && !(opciones?.cargas || []).some((o) => o.id === sp.get("carga")) && <option value={sp.get("carga") || ""}>Carga elegida</option>}
            </select>
          </Filtro>
          <Filtro id={ids.consentimiento} label="Consentimiento">
            <select id={ids.consentimiento} value={sp.get("consentimiento") || ""} onChange={(e) => setFiltro("consentimiento", e.target.value)} className={selectCls}>
              <option value="">Todos</option>
              {CONSENTIMIENTOS.map((o) => <option key={o.valor} value={o.valor}>{o.label}</option>)}
            </select>
          </Filtro>
          <Filtro id={ids.noContactar} label="No contactar">
            <select id={ids.noContactar} value={sp.get("noContactar") || ""} onChange={(e) => setFiltro("noContactar", e.target.value)} className={selectCls}>
              <option value="">Todos</option>
              <option value="si">Sí</option>
              <option value="no">No</option>
            </select>
          </Filtro>
          <Filtro id={ids.sucursal} label="Sucursal">
            <select id={ids.sucursal} value={sp.get("sucursal") || ""} onChange={(e) => setFiltro("sucursal", e.target.value)} className={selectCls}>
              <option value="">Todas</option>
              {(opciones?.sucursales || []).map((o) => <option key={o.clave} value={o.clave}>{o.texto}</option>)}
            </select>
          </Filtro>
        </div>
      </div>

      {cargando && !datos ? (
        <Esqueleto filas={6} alto="h-11" />
      ) : error && !datos ? (
        <CajaError titulo="No pudimos traer los contactos." detalle={error} onReintentar={() => setReintento((n) => n + 1)} />
      ) : !datos || (!datos.items.length && !datos.total) ? (
        // Vacío de verdad (total 0). Una página vacía con total > 0 es una página fuera de rango: va a
        // la tabla, con la paginación y el total, mientras el efecto de arriba la lleva a la última.
        hayFiltros ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
            <p className="text-sm text-slate-600">Ningún contacto coincide con los filtros.</p>
            <button type="button" onClick={limpiar} className="mt-3 text-sm font-semibold text-violet-700 underline">Limpiar filtros</button>
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
            <Users size={22} className="mx-auto text-slate-300" />
            <p className="mt-3 text-sm text-slate-600">Todavía no hay contactos. Subí un Excel en Cargas.</p>
            <Link to={`${R_BASE}/cargas`} className="mt-3 inline-block text-sm font-semibold text-violet-700 underline">Ir a Cargas</Link>
          </div>
        )
      ) : (
        <div className={`space-y-3 ${cargando ? "opacity-60" : ""}`} aria-busy={cargando || undefined}>
          {error && <p className="text-xs text-amber-700">{error} Se muestra lo último que trajimos.</p>}
          <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Nombre</th>
                  <th className="px-4 py-2.5">Teléfono</th>
                  <th className="px-4 py-2.5">Etiquetas</th>
                  <th className="px-4 py-2.5">Consentimiento</th>
                  <th className="px-4 py-2.5">Alta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!datos.items.length && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">Esta página ya no tiene contactos.</td></tr>
                )}
                {datos.items.map((c) => {
                  const ruta = `${R_BASE}/contactos/${encodeURIComponent(c.id)}`;
                  return (
                    <tr key={c.id} onClick={() => navigate(ruta)} className="cursor-pointer hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <Link to={ruta} onClick={(e) => e.stopPropagation()} className="font-semibold text-slate-900 hover:underline">{c.nombre || "Sin nombre"}</Link>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{destinoParaMostrar(c.destino, false)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {c.etiquetas.slice(0, 3).map((e) => <span key={e} className="rounded-full bg-violet-50 text-violet-700 text-[11px] font-semibold px-2 py-0.5">{e}</span>)}
                          {(c.etiquetasMas > 0 || c.etiquetas.length > 3) && (
                            <span className="text-[11px] text-slate-500">+{num(c.etiquetasMas + Math.max(0, c.etiquetas.length - 3))}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <ChipConsentimiento valor={c.consentimiento} />
                        {c.noContactar && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">No contactar</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fecha(c.alta)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <nav aria-label="Páginas" className="flex items-center justify-between gap-3 text-sm">
            <button type="button" onClick={() => irAPagina(page - 1)} disabled={page <= 1}
              className="h-9 px-3 rounded-xl border border-slate-200 bg-white font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              Anterior
            </button>
            <span className="text-slate-600">Página {num(page)} de {num(paginas)} · {num(total)} {total === 1 ? "contacto" : "contactos"}</span>
            <button type="button" onClick={() => irAPagina(page + 1)} disabled={page >= paginas}
              className="h-9 px-3 rounded-xl border border-slate-200 bg-white font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              Siguiente
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}

function Filtro({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="text-[11px] font-semibold text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
